import { Note, NoteMetadata } from "@/types/note";

const NOTES_KEY = "ocnote_notes";
const METADATA_KEY = "ocnote_metadata";

export function getNotes(): Note[] {
  if (typeof window === "undefined") return [];
  const data = localStorage.getItem(NOTES_KEY);
  if (!data) return [];
  try {
    return JSON.parse(data);
  } catch {
    return [];
  }
}

export function saveNote(note: Note): void {
  const notes = getNotes();
  const existingIndex = notes.findIndex((n) => n.id === note.id);

  if (existingIndex >= 0) {
    notes[existingIndex] = note;
  } else {
    notes.unshift(note);
  }

  localStorage.setItem(NOTES_KEY, JSON.stringify(notes));
  updateMetadata(note);
}

export function deleteNote(id: string): void {
  const notes = getNotes();
  const filtered = notes.filter((n) => n.id !== id);
  localStorage.setItem(NOTES_KEY, JSON.stringify(filtered));
}

export function getMetadata(): NoteMetadata {
  if (typeof window === "undefined") {
    return { categories: [], occasions: [], topics: [] };
  }
  const data = localStorage.getItem(METADATA_KEY);
  if (!data) {
    return { categories: [], occasions: [], topics: [] };
  }
  try {
    return JSON.parse(data);
  } catch {
    return { categories: [], occasions: [], topics: [] };
  }
}

function updateMetadata(note: Note): void {
  const metadata = getMetadata();

  if (note.category && !metadata.categories.includes(note.category)) {
    metadata.categories.push(note.category);
  }
  if (note.occasion && !metadata.occasions.includes(note.occasion)) {
    metadata.occasions.push(note.occasion);
  }
  if (note.topic && !metadata.topics.includes(note.topic)) {
    metadata.topics.push(note.topic);
  }

  localStorage.setItem(METADATA_KEY, JSON.stringify(metadata));
}

export function generateId(): string {
  return `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export interface AutoSuggestion {
  category: string;
  occasion: string;
  topic: string;
}

/**
 * Get auto-suggestion for category, occasion, and topic based on:
 * 1. Same day of week from previous week, within 30 min time slot
 * 2. Same day of week from previous week, within 60 min time slot
 * 3. Previous day, within 30 min time slot
 * 4. Previous day, within 60 min time slot
 */
export function getAutoSuggestion(): AutoSuggestion | null {
  const notes = getNotes();
  if (notes.length === 0) return null;

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  // Helper to check if a note is within a time window (in minutes)
  const isWithinTimeWindow = (note: Note, windowMinutes: number): boolean => {
    const noteDate = new Date(note.createdAt);
    const noteMinutes = noteDate.getHours() * 60 + noteDate.getMinutes();
    return Math.abs(noteMinutes - currentMinutes) <= windowMinutes;
  };

  // Helper to get date N days ago at midnight
  const getDaysAgo = (days: number): Date => {
    const date = new Date(now);
    date.setDate(date.getDate() - days);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  // Helper to check if note was created on a specific date
  const isOnDate = (note: Note, targetDate: Date): boolean => {
    const noteDate = new Date(note.createdAt);
    return (
      noteDate.getFullYear() === targetDate.getFullYear() &&
      noteDate.getMonth() === targetDate.getMonth() &&
      noteDate.getDate() === targetDate.getDate()
    );
  };

  // Helper to find matching note for a specific date and time window
  const findMatchingNote = (
    targetDate: Date,
    windowMinutes: number,
  ): Note | null => {
    return (
      notes.find(
        (note) =>
          isOnDate(note, targetDate) &&
          isWithinTimeWindow(note, windowMinutes) &&
          (note.category || note.occasion || note.topic),
      ) || null
    );
  };

  // 1. Check same day of week from previous week (7 days ago)
  const sameDayLastWeek = getDaysAgo(7);

  // Try 30 min window first
  let match = findMatchingNote(sameDayLastWeek, 30);
  if (match) {
    return {
      category: match.category,
      occasion: match.occasion,
      topic: match.topic,
    };
  }

  // Try 60 min window
  match = findMatchingNote(sameDayLastWeek, 60);
  if (match) {
    return {
      category: match.category,
      occasion: match.occasion,
      topic: match.topic,
    };
  }

  // 2. Check previous day (yesterday)
  const yesterday = getDaysAgo(1);

  // Try 30 min window
  match = findMatchingNote(yesterday, 30);
  if (match) {
    return {
      category: match.category,
      occasion: match.occasion,
      topic: match.topic,
    };
  }

  // Try 60 min window
  match = findMatchingNote(yesterday, 60);
  if (match) {
    return {
      category: match.category,
      occasion: match.occasion,
      topic: match.topic,
    };
  }

  return null;
}
